// RoundRobinManagePage — admin manage view. Phase RR1.
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Navigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { CaretLeft, Trophy } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { roundRobinAPI, RR_STATUS_LABELS } from '../lib/roundRobinApi';
import RRStandingsTable from '../components/roundrobin/RRStandingsTable';
import RRMatchList from '../components/roundrobin/RRMatchList';
import RRPoolBuilder from '../components/roundrobin/RRPoolBuilder';
import RRSchedulePanel from '../components/roundrobin/RRSchedulePanel';
import RREventDaySettings from '../components/roundrobin/RREventDaySettings';
import RRChampionshipFormat from '../components/roundrobin/RRChampionshipFormat';
import EntryDisplay from '../components/EntryDisplay';
import EventEditor from '../components/events/EventEditor';
import EventManagerAssignment from '../components/events/EventManagerAssignment';
import CommunicateCard from '../components/events/CommunicateCard';
import AdminRegistrationControlCenter from '../components/events/AdminRegistrationControlCenter';
import { FormatOfPlaySelector } from '../components/scoring/FormatOfPlay';

export default function RoundRobinManagePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, managedEvents } = useAuth();
  const [rr, setRr] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(() => {
    roundRobinAPI.get(id).then((r) => setRr(r.data)).catch((e) => setErr(e.response?.data?.detail || 'Load failed'));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const isAdmin = user?.role === 'admin';
  const isEventManager = Array.isArray(managedEvents) && managedEvents.some(
    (m) => m.event_kind === 'round_robin' && m.event_id === id,
  );
  const canManage = isAdmin || isEventManager;

  if (!user) return <div className="text-sm text-[#6a766e]">Loading…</div>;
  if (!canManage) return <Navigate to={`/round-robins/${id}`} replace />;
  if (err) return <div className="text-sm text-red-700">{err}</div>;
  if (!rr) return <div className="text-sm text-[#6a766e]">Loading…</div>;

  const setStatus = async (s) => { await roundRobinAPI.update(id, { status: s }); load(); };

  return (
    <div className="space-y-5" data-testid="rr-manage">
      <button onClick={() => navigate('/admin/events')} className="text-xs text-[#6a766e] flex items-center gap-1" data-testid="rr-back">
        <CaretLeft size={14} /> Back to events
      </button>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#f3f1ec] text-[#6a766e]" data-testid="rr-status">{RR_STATUS_LABELS[rr.status]}</span>
            <span className="text-[10px] uppercase tracking-wider text-[#9aa39d]">{rr.rr_type === 'singles' ? 'Singles' : 'Doubles'} · {rr.num_pools} pool{rr.num_pools > 1 ? 's' : ''}</span>
          </div>
          <h1 className="font-serif text-3xl text-[#013523] mt-1">{rr.name}</h1>
          <Link to={`/round-robins/${rr.id}`} className="text-xs text-[#015e3f] underline">Public page</Link>
        </div>
        <div className="flex gap-2 flex-wrap">
          {rr.status === 'draft' && <button data-testid="rr-open-registration" onClick={() => setStatus('registration_open')} className="px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800 text-xs">Open registration</button>}
          {rr.status === 'registration_open' && <button data-testid="rr-close-registration" onClick={() => setStatus('registration_closed')} className="px-3 py-1.5 rounded-full bg-amber-100 text-amber-800 text-xs">Close registration</button>}
          {isAdmin && rr.status === 'archived' && (
            <button
              data-testid="rr-restore-btn"
              onClick={async () => {
                try { await roundRobinAPI.restore(id); toast.success('Event restored.'); load(); }
                catch (e) { toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Restore failed'); }
              }}
              className="px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-semibold">
              Restore event
            </button>
          )}
          {isAdmin && (
            <a
              href={`/round-robins/${rr.id}/view`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="rr-manage-view-mode-btn"
              className="inline-flex items-center px-4 py-1.5 rounded-full bg-[#013523] text-white text-xs font-semibold uppercase tracking-[0.14em] hover:bg-[#025a3a] transition-colors">
              View Mode
            </a>
          )}
        </div>
      </div>

      {/* Event Manager — Admin-only assign / change / remove.
          Placed at the top of the page (immediately below the title
          block) so admins never have to scroll past the entire
          EventEditor to discover the assignment control. Matches the
          placement used on Club Event and Tournament pages. */}
      <EventManagerAssignment eventKind="round_robin" eventId={id} isAdmin={isAdmin} />

      {isAdmin && <CommunicateCard eventKind="round_robin" eventId={id} eventName={rr.name} isAdmin={isAdmin} />}

      <EventEditor
        event={rr}
        eventKind="round_robin"
        flyerUrl={rr.flyer_url}
        onSaved={load}
        isAdmin={isAdmin}
        api={{
          updateEvent: (payload) => roundRobinAPI.update(id, payload),
          uploadFlyer: (file) => roundRobinAPI.uploadFlyer(id, file),
          deleteFlyer: () => roundRobinAPI.deleteFlyer(id),
        }}
      />

      <FormatOfPlaySelector
        event={rr}
        testidPrefix="rr-fop"
        onSave={async (payload) => { await roundRobinAPI.update(id, payload); load(); }}
      />

      {/* Registration Control — replaces the old Entries + Alternates +
          Payments trio. Alternates panel is embedded inside. */}
      <AdminRegistrationControlCenter eventKind="round_robin" event={rr} onChanged={load} />

      {/* Pool Builder — RR Update 1 (Feb 2026). Full admin control over
          pool assignments, ordering, naming, and team names. */}
      <RRPoolBuilder rr={rr} onChanged={load} />

      {isAdmin && <RRChampionshipFormat rr={rr} onChanged={load} />}

      {/* Event Day Settings — RR Update 3. Courts / duration / rest / window. */}
      <RREventDaySettings rr={rr} onChanged={load} />

      {/* Schedule Panel — RR Update 2/3. Mode picker + Generate + Auto-schedule + Publish + Reset. */}
      <RRSchedulePanel rr={rr} onChanged={load} />

      {(rr.matches.length > 0 || (user.role === 'admin' && ['schedule_generated'].includes(rr.status))) && (
        <RRMatchList rr={rr} rrId={id} onChanged={load} admin={isAdmin} canScore={canManage} />
      )}
      {rr.matches.length > 0 && rr.standings && Object.keys(rr.standings).length > 0 && (
        <RRStandingsTable rr={rr} />
      )}

      {rr.champion && (
        <div className="border border-[#c9a93a] rounded-2xl p-4 bg-gradient-to-br from-[#fffbf0] to-[#fff7e0]" data-testid="rr-champion">
          <div className="flex items-center gap-2 text-[#c9a93a]"><Trophy size={18} weight="fill" /><span className="text-xs uppercase font-semibold">Champion</span></div>
          <div className="mt-1 text-lg text-[#013523] font-semibold"><EntryDisplay entry={rr.champion} /></div>
          {rr.runner_up && <div className="text-xs text-[#6a766e]">Runner-up: <EntryDisplay entry={rr.runner_up} /></div>}
        </div>
      )}
    </div>
  );
}
